import CopyCode from "../CopyCode/CopyCode";
import Icon from "../Icon";

const TypographyComponent = () => {
	return (
		<div className="flex flex-col gap-y-10 p-6">
			{/* SECTION 1: HEADINGS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-white mb-6">Headings</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 1: H1 Styles */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">H1 Heading</label>
						<h1 className="text-4xl font-bold text-amber-400 mb-2">Large Heading</h1>
						<h1 className="text-4xl font-bold bg-gradient-to-r from-amber-400 to-blue-400 bg-clip-text text-transparent mb-2">Gradient H1</h1>
						<CopyCode number_code={1} code={`<h1 className="text-4xl font-bold text-amber-400">Large Heading</h1>`} />
					</div>

					{/* Variation 2: H2/H3 Styles */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">H2 & H3 Headings</label>
							<h2 className="text-3xl font-bold text-white mb-2">Section Heading</h2>
							<h3 className="text-2xl font-semibold text-amber-400 mb-3">Subsection</h3>
							<CopyCode number_code={2} code={`<h2 className="text-3xl font-bold text-white">Section</h2>
<h3 className="text-2xl font-semibold text-amber-400">Subsection</h3>`} />
					</div>

					{/* Variation 3: H4/H5/H6 */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">H4/H5/H6 Headings</label>
						<h4 className="text-xl font-semibold text-amber-400 mb-2">Heading 4</h4>
						<h5 className="text-lg font-semibold text-amber-300 mb-2">Heading 5</h5>
						<h6 className="text-base font-semibold text-slate-300">Heading 6</h6>
						<CopyCode number_code={3} code={`<h4 className="text-xl font-semibold text-amber-400">H4</h4>
<h5 className="text-lg font-semibold text-amber-300">H5</h5>
<h6 className="text-base font-semibold text-slate-300">H6</h6>`} />
					</div>

					{/* Variation 4: With Icons */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Headings with Icons</label>
						<div className="space-y-3">
							<h3 className="text-2xl font-bold text-amber-400 flex items-center gap-2"><Icon name="palette" className="w-5 h-5" /> Design</h3>
							<h3 className="text-2xl font-bold text-amber-400 flex items-center gap-2"><Icon name="laptop" className="w-5 h-5" /> Development</h3>
							<h3 className="text-2xl font-bold text-amber-400 flex items-center gap-2"><Icon name="mobile" className="w-5 h-5" /> Mobile</h3>
						</div>
						<CopyCode number_code={4} code={`<h3 className="text-2xl font-bold text-amber-400 flex items-center gap-2">
	<span>Icon</span> Design
</h3>`} />
					</div>

					{/* Variation 5: Highlighted Headings */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Highlighted Headings</label>
						<h2 className="text-3xl font-bold">Build <span className="bg-amber-400/20 text-amber-400 px-2 rounded">Amazing</span> Apps</h2>
						<h2 className="text-3xl font-bold mt-3">Get <span className="underline decoration-amber-400 underline-offset-4">Started</span> Today</h2>
						<CopyCode number_code={5} code={`<h2 className="text-3xl font-bold">
  Build <span className="bg-amber-400/20 text-amber-400 px-2 rounded">Amazing</span> Apps
</h2>`} />
					</div>

					{/* Variation 6: Truncated Headings */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Text Truncation</label>
						<h3 className="text-xl font-bold text-slate-100 truncate">This is a very long heading that will be truncated with ellipsis</h3>
						<h3 className="text-lg font-semibold text-slate-200 line-clamp-2 mt-3">This is a longer heading that spans multiple lines but gets limited to just two lines maximum and then truncated</h3>
						<CopyCode number_code={6} code={`<h3 className="text-xl font-bold text-slate-100 truncate">
  Long heading...
</h3>

<h3 className="text-lg font-semibold text-slate-200 line-clamp-2">
  Multi-line heading truncated...
</h3>`} />
					</div>

				</div>
			</div>

			{/* SECTION 2: TEXT STYLES */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="pencil" className="w-5 h-5" /> Text Styles</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 7: Paragraph Styles */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Paragraph Styles</label>
						<p className="text-slate-300 leading-relaxed mb-3">This is a regular paragraph with relaxed line height. Lorem ipsum dolor sit amet consectetur adipisicing elit.</p>
						<p className="text-slate-400 leading-tight text-sm mb-3">This is a tight paragraph. Good for body text.</p>
						<p className="text-slate-500 leading-loose italic opacity-75">This is an italic paragraph with loose spacing.</p>
						<CopyCode number_code={7} code={`<p className="text-slate-300 leading-relaxed">Regular paragraph</p>
<p className="text-slate-400 leading-tight text-sm">Tight paragraph</p>
<p className="text-slate-500 leading-loose italic">Italic paragraph</p>`} />
					</div>

					{/* Variation 8: Text Emphasis */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Text Emphasis</label>
						<p className="text-slate-300 mb-2"><strong className="font-bold text-amber-400">Bold text</strong> for emphasis</p>
						<p className="text-slate-300 mb-2"><em className="italic text-amber-400">Italic text</em> for hints</p>
						<p className="text-slate-300 mb-2"><mark className="bg-amber-400/20 text-amber-300 px-1 rounded">Highlighted text</mark> for focus</p>
						<p className="text-slate-300"><code className="bg-slate-700 px-2 py-1 rounded text-sm text-amber-300 font-mono">code snippet</code></p>
						<CopyCode number_code={8} code={`<strong className="font-bold text-amber-400">Bold</strong>
<em className="italic text-amber-400">Italic</em>
<mark className="bg-amber-400/20 text-amber-300">Highlight</mark>
<code className="bg-slate-700 text-amber-300">code</code>`} />
					</div>

					{/* Variation 9: Text Colors */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Text Colors</label>
						<p className="text-amber-400 mb-1">Cyan text</p>
						<p className="text-amber-400 mb-1">Purple text</p>
						<p className="text-amber-400 mb-1">Pink text</p>
						<p className="text-slate-300 mb-1">Default text</p>
						<p className="text-slate-500">Muted text</p>
						<CopyCode number_code={9} code={`<p className="text-amber-400">Cyan text</p>
<p className="text-amber-400">Purple text</p>
<p className="text-amber-400">Pink text</p>
<p className="text-slate-500">Muted text</p>`} />
					</div>

					{/* Variation 10: Links */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Link Styles</label>
						<a href="#" className="text-amber-400 hover:text-amber-300 underline">Standard link</a>
						<br className="my-2" />
						<a href="#" className="text-amber-400 hover:text-amber-300 hover:underline underline-offset-2">Link with offset</a>
						<br className="my-2" />
						<a href="#" className="text-amber-400 hover:text-amber-300 hover:no-underline font-semibold">Bold link</a>
						<CopyCode number_code={10} code={`<a href="#" className="text-amber-400 hover:text-amber-300 underline">Link</a>
<a href="#" className="text-amber-400 hover:underline underline-offset-2">Link</a>
<a href="#" className="text-amber-400 font-semibold">Link</a>`} />
					</div>

					{/* Variation 11: Lists */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">List Styles</label>
						<ul className="list-disc list-inside text-slate-300 space-y-1 mb-3">
							<li>Bullet point one</li>
							<li>Bullet point two</li>
							<li>Bullet point three</li>
						</ul>
						<ol className="list-decimal list-inside text-slate-400 space-y-1 text-sm">
							<li>Numbered item</li>
							<li>Second item</li>
							<li>Third item</li>
						</ol>
						<CopyCode number_code={11} code={`<ul className="list-disc list-inside text-slate-300 space-y-1">
  <li>Bullet point</li>
  <li>Another point</li>
</ul>

<ol className="list-decimal list-inside">
  <li>First item</li>
  <li>Second item</li>
</ol>`} />
					</div>

					{/* Variation 12: Blockquote */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Blockquote</label>
						<blockquote className="border-l-4 border-l-amber-500 pl-4 italic text-slate-300 bg-slate-800/50 p-3 rounded">
							"The only way to do great work is to love what you do." — Steve Jobs
						</blockquote>
						<CopyCode number_code={12} code={`<blockquote className="border-l-4 border-l-amber-500 pl-4 italic text-slate-300 bg-slate-800/50 p-3 rounded">
  "Great quote here"
</blockquote>`} />
					</div>

				</div>
			</div>

			{/* SECTION 3: TEXT EFFECTS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="sparkles" className="w-5 h-5" /> Text Effects</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 13: Text Shadow */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Text Shadows</label>
						<h3 className="text-2xl font-bold text-amber-400 drop-shadow-lg mb-2">Drop Shadow</h3>
						<h3 className="text-2xl font-bold text-amber-400" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.5)'}}>Custom Shadow</h3>
						<CopyCode number_code={13} code={`<h3 className="text-2xl font-bold text-amber-400 drop-shadow-lg">
  Drop Shadow
</h3>

<h3 className="text-2xl font-bold" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.5)'}}>
  Custom Shadow
</h3>`} />
					</div>

					{/* Variation 14: Gradient Text */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Gradient Text</label>
						<h3 className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-blue-400 bg-clip-text text-transparent mb-2">Cyan to Blue</h3>
						<h3 className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-amber-400 bg-clip-text text-transparent">Purple to Pink</h3>
						<CopyCode number_code={14} code={`<h3 className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-blue-400 bg-clip-text text-transparent">
  Gradient Text
</h3>`} />
					</div>

					{/* Variation 15: Animated Text */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Animated Text</label>
						<p className="text-amber-400 animate-pulse text-lg font-semibold mb-2">Pulsing text</p>
						<p className="text-amber-400 animate-bounce text-lg font-semibold">Bouncing text</p>
						<CopyCode number_code={15} code={`<p className="text-amber-400 animate-pulse">Pulsing</p>
<p className="text-amber-400 animate-bounce">Bouncing</p>`} />
					</div>

					{/* Variation 16: Underlines & Decorations */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Decorations</label>
						<p className="text-slate-300 underline mb-1">Underlined text</p>
						<p className="text-slate-300 line-through mb-1">Strikethrough text</p>
						<p className="text-slate-300 underline decoration-wavy decoration-2">Wavy underline</p>
						<CopyCode number_code={16} code={`<p className="text-slate-300 underline">Underlined</p>
<p className="text-slate-300 line-through">Strikethrough</p>
<p className="text-slate-300 underline decoration-wavy">Wavy</p>`} />
					</div>

					{/* Variation 17: Text Transform */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Text Transform</label>
						<p className="uppercase text-slate-300 mb-1 tracking-widest">Uppercase text</p>
						<p className="lowercase text-slate-300 mb-1">LOWERCASE TEXT</p>
						<p className="capitalize text-slate-300">capitalize each word</p>
						<CopyCode number_code={17} code={`<p className="uppercase tracking-widest">Uppercase</p>
<p className="lowercase">LOWERCASE</p>
<p className="capitalize">capitalize words</p>`} />
					</div>

					{/* Variation 18: Spacing */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Letter & Word Spacing</label>
						<p className="text-slate-300 tracking-tight mb-1">Tight letter spacing</p>
						<p className="text-slate-300 tracking-normal mb-1">Normal letter spacing</p>
						<p className="text-slate-300 tracking-widest">Wide letter spacing</p>
						<CopyCode number_code={18} code={`<p className="text-slate-300 tracking-tight">Tight</p>
<p className="text-slate-300 tracking-normal">Normal</p>
<p className="text-slate-300 tracking-widest">Wide</p>`} />
					</div>

				</div>
			</div>
		</div>
	);
};

export default TypographyComponent;



